import React from 'react';
import Icon from 'components/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const SystemMonitoring = ({ detailed = false }) => {
  // Mock system metrics data
  const performanceData = [
    { time: '00:00', cpu: 45, memory: 62, requests: 120 },
    { time: '04:00', cpu: 32, memory: 58, requests: 85 },
    { time: '08:00', cpu: 68, memory: 71, requests: 245 },
    { time: '12:00', cpu: 75, memory: 78, requests: 320 },
    { time: '16:00', cpu: 82, memory: 81, requests: 380 },
    { time: '20:00', cpu: 58, memory: 69, requests: 210 }
  ];

  const systemHealth = [
    {
      id: 1,
      name: 'API Server',
      status: 'healthy',
      uptime: '99.98%',
      responseTime: '45ms',
      icon: 'Server'
    },
    {
      id: 2,
      name: 'Database',
      status: 'healthy',
      uptime: '99.99%',
      responseTime: '12ms',
      icon: 'Database'
    },
    {
      id: 3,
      name: 'Storage',
      status: 'healthy',
      uptime: '100%',
      responseTime: '8ms',
      icon: 'HardDrive'
    },
    {
      id: 4,
      name: 'Authentication',
      status: 'healthy',
      uptime: '99.95%',
      responseTime: '32ms',
      icon: 'Shield'
    }
  ];

  const alerts = [
    {
      id: 1,
      type: 'warning',
      message: 'High memory usage detected on server-2',
      time: '5 minutes ago'
    },
    {
      id: 2,
      type: 'info',
      message: 'Database backup completed successfully',
      time: '1 hour ago'
    },
    {
      id: 3,
      type: 'success',
      message: 'System update deployed successfully',
      time: '3 hours ago'
    }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      healthy: { bg: 'bg-success-100', text: 'text-success-700', label: 'Healthy', icon: 'CheckCircle' },
      warning: { bg: 'bg-warning-100', text: 'text-warning-700', label: 'Warning', icon: 'AlertTriangle' },
      error: { bg: 'bg-error-100', text: 'text-error-700', label: 'Error', icon: 'XCircle' }
    };
    const config = statusConfig?.[status] || statusConfig?.healthy;
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        <Icon name={config?.icon} size={12} />
        <span>{config?.label}</span>
      </span>
    );
  };

  const getAlertIcon = (type) => {
    const iconMap = {
      warning: { icon: 'AlertTriangle', color: 'text-warning-600' },
      error: { icon: 'AlertCircle', color: 'text-error' },
      info: { icon: 'Info', color: 'text-primary' },
      success: { icon: 'CheckCircle', color: 'text-success' }
    };
    return iconMap?.[type] || iconMap?.info;
  };

  return (
    <div className="space-y-6">
      {/* System Health Cards */}
      <div className="bg-surface rounded-lg shadow-card p-6">
        <h2 className="text-xl font-heading font-bold text-text-primary mb-4">System Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {systemHealth?.map((service) => (
            <div key={service?.id} className="p-4 border border-border rounded-lg hover:shadow-md transition-smooth">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                    <Icon name={service?.icon} size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-text-primary">{service?.name}</h3>
                    <p className="text-xs text-text-secondary">Uptime: {service?.uptime}</p>
                  </div>
                </div>
                {getStatusBadge(service?.status)}
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-secondary">Response Time</span>
                <span className="font-medium text-text-primary">{service?.responseTime}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Charts */}
      {detailed && (
        <div className="bg-surface rounded-lg shadow-card p-6">
          <h2 className="text-xl font-heading font-bold text-text-primary mb-4">Performance Metrics</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="cpu" stroke="#8B5CF6" name="CPU %" />
                <Line type="monotone" dataKey="memory" stroke="#10B981" name="Memory %" />
                <Line type="monotone" dataKey="requests" stroke="#F59E0B" name="Requests" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Recent Alerts */}
      <div className="bg-surface rounded-lg shadow-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-heading font-bold text-text-primary">Recent Alerts</h2>
          <button className="text-primary text-sm font-medium hover:underline">
            View All
          </button>
        </div>
        <div className="space-y-3">
          {alerts?.map((alert) => {
            const alertIcon = getAlertIcon(alert?.type);
            return (
              <div key={alert?.id} className="flex items-start space-x-3 p-3 border border-border rounded-lg hover:bg-secondary-50 transition-smooth">
                <Icon name={alertIcon?.icon} size={20} className={alertIcon?.color} />
                <div className="flex-1">
                  <p className="text-sm text-text-primary">{alert?.message}</p>
                  <p className="text-xs text-text-secondary mt-1">{alert?.time}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SystemMonitoring;